/* @jsxImportSource @emotion/react */
import React from 'react'
import { css } from '@emotion/react'
import classNames from 'classnames'
import { FormattedMessage } from 'react-intl'

import { CustomerLogoBanner, ImgSanity } from '@slite/components'

import ComplianceLogos from '../../../assets/compliance-logos.svg'
import { Button } from '../../../components/Button/Button'
import { Section } from '../../../components/Section'
import { SocialProofs } from '../../../components/SocialProofs'
import { mediaQuerySmall } from '../../../components/style'
import { WebsiteText } from '../../../components/WebsiteText'
import { useRoutes } from '../../../routes'

type SectionSolutionsHeroProps = {
  title: string
  description: string
  teamName: string
  imageUrl: string
  illustrationUrl: string | null
  illustrationSmall: string | null
  brandColor: string
}

export function SectionSolutionsHero(props: SectionSolutionsHeroProps) {
  const routes = useRoutes()
  return (
    <Section
      isFirstSection
      containerTopPadding={80}
      containerBottomPadding={80}
      background="moon-dust-lighter"
      className="position-relative"
    >
      <div
        className={classNames(
          'flexbox flexbox-column flexbox-align-center text-align-center',
          'margin-left-auto margin-right-auto position-relative'
        )}
      >
        <WebsiteText
          variant="heroTitle"
          className="text-align-center position-relative"
          css={css`
            && {
              font-size: 64px;
              max-width: 900px;
              margin-left: auto;
              margin-right: auto;
              line-height: 1.2;

              ${mediaQuerySmall} {
                font-size: var(--font-size-1);
              }
            }
          `}
        >
          {props.title}
        </WebsiteText>
        <p
          className="font-size-4 line-height-large margin-top-16 padding-top-24 shade-10"
          style={{ maxWidth: 600 }}
        >
          {props.description}
        </p>
        <div
          className="margin-top-32 flexbox flexbox-align-center flexbox-gap-12"
          css={{
            [mediaQuerySmall]: {
              flexDirection: 'column',
              gap: 16,
            },
          }}
        >
          <Button variant="primary" size="huge" href={routes.bookDemo()}>
            <FormattedMessage
              id="solutions.hero.cta.bookdemo"
              defaultMessage="Book a demo"
            />
          </Button>
          <img
            // eslint-disable-next-line formatjs/no-literal-string-in-jsx
            alt="AICPA SOC 2 logo"
            src={ComplianceLogos}
            style={{ maxWidth: 200 }}
          />
        </div>
        {props.illustrationUrl && (
          <div
            className="position-absolute display-none-small display-none-medium"
            style={{
              bottom: -190,
              left: 20,
              height: 200,
              width: 200,
            }}
          >
            <ImgSanity
              className="width-full height-full"
              src={props.illustrationUrl}
              alt="Hero illustration"
              style={{ objectFit: 'contain' }}
            />
          </div>
        )}
        {props.illustrationSmall && (
          <div
            className="position-absolute display-none-small display-none-medium"
            style={{
              top: 160,
              right: 20,
              height: 140,
              width: 140,
            }}
          >
            <ImgSanity
              className="width-full height-full"
              src={props.illustrationSmall}
              alt="Hero illustration small"
              style={{ objectFit: 'contain' }}
            />
          </div>
        )}
      </div>

      <div className="width-full flexbox flexbox-justify-center margin-top-32">
        <SocialProofs color={props.brandColor} variant="center" />
      </div>

      <ImgSanity
        className="width-full margin-top-40 overflow-hidden"
        src={props.imageUrl}
        alt="Hero image"
        style={{
          borderRadius: 32,
          border: '1px solid rgba(0, 0, 0, 0.05)',
        }}
      />

      <div className="padding-top-40">
        <p className="text-align-center font-size-4 line-height-large shade-10">
          <FormattedMessage
            id="mcp.hero.customer.caption"
            defaultMessage="1,700+ leading companies trust Slite as their single source of truth."
          />
        </p>
        <div
          className="margin-left-auto margin-right-auto"
          style={{ maxWidth: 900 }}
        >
          <CustomerLogoBanner />
        </div>
      </div>
    </Section>
  )
}
